<template>
  <div class="contract-dates-section">
    <div class="contract-dates-section__row">
      <div class="contract-dates-section__field">
        <label for="ppa-contract-start-date" class="form-label">
          <FontAwesomeIcon icon="calendar" class="me-1" />
          {{ $t("common.start_date") }}
        </label>
        <input
          type="date"
          class="form-control"
          id="ppa-contract-start-date"
          :value="actionInstructions.start_date ?? ''"
          @change="onStartDateChange"
          @blur="v$.start_date.$touch()"
          :class="{ 'is-invalid': startDateHasError }"
        />
        <div v-if="startDateHasError" class="invalid-feedback d-block">
          <div v-for="error in startDateErrors" :key="error">
            {{ error }}
          </div>
        </div>
        <div v-if="partialPeriodHint" class="form-text text-info">
          <FontAwesomeIcon icon="info-circle" class="me-1" />
          {{ partialPeriodHint }}
        </div>
      </div>

      <div class="contract-dates-section__field">
        <label for="ppa-contract-end-date" class="form-label">
          <FontAwesomeIcon icon="calendar" class="me-1" />
          {{ $t("common.end_date") }}
        </label>
        <input
          type="date"
          class="form-control"
          id="ppa-contract-end-date"
          :value="actionInstructions.end_date ?? ''"
          @change="onEndDateChange"
          @blur="v$.end_date.$touch()"
          :class="{ 'is-invalid': endDateHasError }"
        />
        <div v-if="endDateHasError" class="invalid-feedback d-block">
          <div v-for="error in endDateErrors" :key="error">
            {{ error }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import {
  snapToMonthEnd,
  snapToMonthStart,
} from "@/dashboard/production_strategy/overview/PPA/ppaActionUtils";
import {
  describePartialBoundary,
  parseIsoDate,
} from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";
import type { ActionInstructions, LimitGranularity } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const actionInstructions = defineModel<ActionInstructions>({ required: true });

const props = defineProps<{
  /** Current allocation granularity — drives the partial-period hint and
   *  the "start date required" validation. `null`/undefined means no
   *  allocation has been configured yet, in which case we deliberately
   *  suppress the hint (no cadence to be partial against) and skip the
   *  granular-limits start-date requirement. */
  granularity?: LimitGranularity | null;
}>();

const { t } = useI18n();
const customValidators = useValidators(t);

const limitGranularity = computed<LimitGranularity | null>(
  () => props.granularity ?? null
);

// Cross-field check used by the end-date validator and to silence the partial
// period hint when the user's range is reversed mid-edit. Presence is enforced
// separately so we don't double-report "required" as "must follow start".
const isRangeInverted = computed(() => {
  const rawStart = actionInstructions.value.start_date;
  const rawEnd = actionInstructions.value.end_date;
  if (!rawStart || !rawEnd) return false;
  const start = parseIsoDate(rawStart);
  const end = parseIsoDate(rawEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
    return false;
  return end.getTime() < start.getTime();
});

const rules = computed(() => ({
  end_date: {
    required: customValidators.required,
    afterStart: helpers.withMessage(
      t("portfolio_instructions.end_date_must_follow_start"),
      (value: string | null | undefined) => {
        const rawStart = actionInstructions.value.start_date;
        if (!value || !rawStart) return true;
        const start = parseIsoDate(rawStart);
        const end = parseIsoDate(value);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return true;
        }
        return end.getTime() >= start.getTime();
      }
    ),
  },
  start_date: {
    requiredForGranularLimits: helpers.withMessage(
      t("portfolio_instructions.start_date_required_for_granularity"),
      (value: string | null | undefined) => {
        const g = limitGranularity.value;
        if (g !== "monthly" && g !== "quarterly") return true;
        return Boolean(value);
      }
    ),
  },
}));

const v$ = useVuelidate(
  rules,
  computed(() => ({
    end_date: actionInstructions.value.end_date,
    start_date: actionInstructions.value.start_date,
  })),
  { $autoDirty: true }
);

const endDateHasError = computed(() => v$.value.end_date.$error);
const startDateHasError = computed(() => v$.value.start_date.$error);

const endDateErrors = computed(
  () => v$.value.end_date.$errors?.map((e) => e.$message as string) || []
);
const startDateErrors = computed(
  () => v$.value.start_date.$errors?.map((e) => e.$message as string) || []
);

// Hint that surfaces a partial first/last period when the contract's
// start/end fall inside a calendar period for the current granularity. The
// matrix itself shows a "Partial" badge per year; this echoes the range
// next to the date input so the user sees the consequence without scrolling.
const partialPeriodHint = computed(() => {
  const granularity = limitGranularity.value;
  if (!granularity) return "";
  // Suppress the hint while the range is reversed — the user will see the
  // cross-field validation error instead, and `describePartialBoundary` on
  // an inverted range produces meaningless output.
  if (isRangeInverted.value) return "";
  const rawStart = actionInstructions.value.start_date;
  const rawEnd = actionInstructions.value.end_date;
  if (!rawStart || !rawEnd) return "";
  const start = parseIsoDate(rawStart);
  const end = parseIsoDate(rawEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const description = describePartialBoundary(start, end, granularity);
  if (!description) return "";
  if (description.count === 1) {
    return t("portfolio_instructions.partial_period_hint_single", description);
  }
  return t("portfolio_instructions.partial_period_hint_multiple", description);
});

// Use @change (fires once per commit) rather than @input (fires per digit
// during manual typing). The latter was clobbering partial keystrokes with
// null and preventing picker selections from sticking, since intermediate
// native dates are reported as "" by the browser.
const onStartDateChange = (event: Event) => {
  const raw = (event.target as HTMLInputElement).value;
  actionInstructions.value = {
    ...actionInstructions.value,
    start_date: snapToMonthStart(raw),
  };
};

const onEndDateChange = (event: Event) => {
  const raw = (event.target as HTMLInputElement).value;
  actionInstructions.value = {
    ...actionInstructions.value,
    end_date: snapToMonthEnd(raw) ?? "",
  };
};

defineExpose({
  isValid: () => !v$.value.$invalid,
  touchAndValidate: () => {
    v$.value.$touch();
    return !v$.value.$invalid;
  },
});
</script>

<style scoped lang="scss">
.contract-dates-section__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 520px) {
  .contract-dates-section__row {
    grid-template-columns: 1fr;
  }
}
</style>
